import Filters from "@/store/utils/filters";
import { getLegendData } from "@/store/utils/legend";
import { authorNameBy } from "@/components/utils/map";
import { get, has } from "lodash";
import { checkShowLicense } from "@/store/utils/helper";

import * as VisualizationActions from "@/store/actions/visualizations";
import * as VisualizationMutations from "@/store/mutations/visualizations";

const DEFAULT_VALUES = {
  filter: "mine",
  order: "updated_at",
  orderDirection: "desc",
  filterKeyword: "",
};

const maps = {
  namespaced: true,

  state: {
    isFetching: false,
    isFiltered: false,
    isErrored: false,
    error: {},
    filterType: DEFAULT_VALUES.filter,
    filterKeyword: DEFAULT_VALUES.filterKeyword,
    order: DEFAULT_VALUES.order,
    orderDirection: DEFAULT_VALUES.orderDirection,
    list: {},
    addedList: {},
    orderList: [],
    metadata: {},
    page: 1,
    numPages: 1,
    resultsPerPage: 12,
  },
  getters: {
    topVisibleMap: (state) => {
      const maps = state.orderList.filter(m => (!!m.visible && (m.permission || m.layerServiceInfo)));
      const map = maps[0]
      if (map) {
        const isDDE = !!map.layerServiceInfo
        return {
          id: map.id,
          title: map.display_name || map.name,
          baseUrl: isDDE ? null : get(map, "permission.owner.base_url"),
          vizUrl: isDDE ? null : `${get(map, "permission.owner.base_url")}/api/v3/viz/${map.id}/viz`,
          wmsUrl: isDDE ? map.layerServiceInfo.url : `${get(map, "permission.owner.base_url")}/visualization_tiles/${map.id}/{z}/{x}/{y}.png?api_key=${get(map, "permission.owner.api_key")}`,
          show_license: map.show_license || checkShowLicense(map),
          author: authorNameBy(map),
        }
      }
    },

    cartoMapsAdd(state) {
      const maps = Object.values(state.orderList.filter(m => m.permission && m.visible))
        .map((panel) => ({
          id: panel.id,
          title: panel.display_name || panel.name,
          baseUrl: get(panel, "permission.owner.base_url"),
          vizUrl: `${get(panel, "permission.owner.base_url")}/api/v3/viz/${panel.id}/viz`,
          wmsUrl: `${get(panel, "permission.owner.base_url")}/visualization_tiles/${panel.id}/{z}/{x}/{y}.png?api_key=${get(panel, "permission.owner.api_key")}`,
          privacy: panel.privacy,
        }))
        .filter((m) => m.baseUrl)
      return maps;
    },

    mapsLegends(state) {
      return state.orderList.map((panel) => {
        return getLegendData(panel);
      })
    },

    getMapVisible: (state) => (mapId) => {
      const map = state.orderList.find(m => m.id == mapId)
      const hasVisibleProp = has(map, "visible");
      if (hasVisibleProp) {
        return map.visible;
      } else {
        return true;
      }
    },

    getVisibleLayers: (state) => (visId) => {
      const isVisualizationPresent = has(
        state.addedList,
        visId
      );

      const visibleLayers = [];
      if (isVisualizationPresent) {
        const layers = state.addedList[visId].layers;
        if (layers) {
          for (const l of layers) {
            if (l.options.visible) {
              visibleLayers.push(l);
            }
          }
        }
      }
      return visibleLayers;
    },
  },
  mutations: {
    setFetchingState: VisualizationMutations.setFetchingState,
    setFilterType: VisualizationMutations.setFilterType,
    setFilterKeyword: VisualizationMutations.setFilterKeyword,
    setOrder: VisualizationMutations.setOrder(DEFAULT_VALUES),
    setPagination: VisualizationMutations.setPagination,
    setResultsPerPage: VisualizationMutations.setResultsPerPage,
    setRequestError: VisualizationMutations.setRequestError,
    setVisualizations: VisualizationMutations.setVisualizations,
    updateVisualization: VisualizationMutations.updateVisualization,
    setMapLaysers: VisualizationMutations.setMapLaysers,
    toggleMapLayserVisible: VisualizationMutations.toggleMapLayserVisible,
    toggleMapVisible: VisualizationMutations.toggleMapVisible,
    addToEarth: VisualizationMutations.addToEarth,
    removeFromEarth: VisualizationMutations.removeFromEarth,
    setOrderList: VisualizationMutations.setOrderList,
    addToOrderList: VisualizationMutations.addToOrderList,
    removeFromOrderList: VisualizationMutations.removeFromOrderList,
    removeVis: VisualizationMutations.removeVis
  },
  actions: {
    filter: VisualizationActions.filter,
    order: VisualizationActions.order,
    resetFilters: VisualizationActions.resetFilters(DEFAULT_VALUES),
    setResultsPerPage: VisualizationActions.setResultsPerPage,
    setPagination: VisualizationActions.setPagination,
    setURLOptions: VisualizationActions.setURLOptions,
    updateVisualization: VisualizationActions.updateVisualization,
    toggleMapLayserVisible: VisualizationActions.toggleMapLayserVisible,
    toggleMapVisible: VisualizationActions.toggleMapVisible,
    addToEarth: VisualizationActions.addToEarth,
    removeFromEarth: VisualizationActions.removeFromEarth,
    setOrderList: VisualizationActions.setOrderList,
    removeVis: VisualizationActions.removeVis,

    fetch(context) {
      const params = {
        ...Filters[context.state.filterType],
        page: context.state.page,
        order: context.state.order,
        q: ["1:5M Geological Map", context.state.filterKeyword]
          .filter(Boolean)
          .join(" "),
        order_direction: context.state.orderDirection,
        per_page: context.state.resultsPerPage,
        show_auth_tokens: true,
        show_user: true
      };

      VisualizationActions.fetchVisualizations(context, params);
    },
  },
};

export default maps;
